import { useEffect, useRef, useState } from "react";
import { GALLERY, bounds } from "@/content/heroGallery";
import { useSiteMediaState } from "@/lib/site";

const T = 11; // ciclo en segundos
const GAP_CM = 1.2; // separación lámina-pared que da el soporte trasero
const EDGE_CM = 0.25; // canto de la lámina
const clamp01 = (v) => Math.min(1, Math.max(0, v));
const easeOut = (t) => 1 - Math.pow(1 - t, 3);
const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

// Estado de cada lámina en el instante t
const pieceState = (i, n, t, isDemo, reduced) => {
  if (reduced) return { op: 1, z: 0, lift: 0, sweep: -1 };
  const a = 1 + 0.5 * i;
  const appear = clamp01((t - a) / 0.5);
  const approach = easeOut(clamp01((t - a) / 0.9));
  const fade = clamp01((t - (9.6 + 0.08 * (n - 1 - i))) / 0.6);
  const op = appear * (1 - fade);
  let z = (1 - approach) * 1;
  let lift = 0;
  if (isDemo) {
    const out = easeInOut(clamp01((t - 7.5) / 0.7));
    const back = easeOut(clamp01((t - 8.6) / 0.8));
    const d = out * (1 - back);
    z += d * 0.45;
    lift = d;
  }
  const s = (t - (5.6 + 0.15 * i)) / 2;
  return { op, z, lift, sweep: s >= 0 && s <= 1 ? s : -1 };
};

const useClock = (reduced) => {
  const frozen = new URLSearchParams(window.location.search).get("heroT");
  const [t, setT] = useState(frozen ? parseFloat(frozen) : 0);
  useEffect(() => {
    if (reduced || frozen) return;
    let raf, start = performance.now();
    const loop = (now) => { setT(((now - start) / 1000) % T); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    const vis = () => { if (!document.hidden) start = performance.now() - t * 1000; };
    document.addEventListener("visibilitychange", vis);
    return () => { cancelAnimationFrame(raf); document.removeEventListener("visibilitychange", vis); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduced]);
  return t;
};

const Piece = ({ p, s, st, gapPx }) => {
  const w = p.w * s, h = p.h * s;
  const r = Math.max(3, Math.min(w, h) * 0.035);
  const edge = Math.max(1.5, EDGE_CM * s);
  const zFront = gapPx + st.z * 42 * (s / 4);
  const lift = st.lift * h * (st.liftK || 0.42);
  const sh = { dx: 3 + zFront * 0.45, dy: 5 + zFront * 0.5 - lift, blur: 4 + zFront * 0.55, a: Math.max(0.12, 0.42 - zFront * 0.004) };
  // soporte real: bloque de aluminio cepillado (~7 × 3 cm) centrado en la parte trasera
  const sup = { w: Math.min(w * 0.45, 7 * s), h: Math.min(h * 0.2, 3 * s) };
  return (
    <div data-testid={`hero-piece-${p.id}`} className="absolute" style={{ left: 0, top: 0, width: w, height: h, transform: `translate3d(${p.x * s}px, ${p.y * s}px, 0)`, transformStyle: "preserve-3d" }}>
      <div className="absolute inset-0" style={{ borderRadius: r, background: `rgba(30,24,18,${sh.a})`, filter: `blur(${sh.blur}px)`, opacity: st.op, transform: `translate3d(${sh.dx}px, ${sh.dy}px, 0.5px)` }} />
      <div data-testid={`hero-support-${p.id}`} className="absolute" style={{ left: (w - sup.w) / 2, top: h * 0.6 - sup.h / 2, width: sup.w, height: sup.h, transformStyle: "preserve-3d", opacity: st.op }}>
        <div className="absolute inset-0 grid place-items-center rounded-[2px]" style={{ background: "linear-gradient(160deg,#e2e0db,#a9a6a0 60%,#c9c6c0)", boxShadow: "1px 2px 3px rgba(0,0,0,.3)", transform: `translateZ(${gapPx - 0.5}px)` }}>
          <span className="block rounded-full bg-[#5b5955]" style={{ width: Math.max(2, s * 0.35), height: Math.max(2, s * 0.35) }} />
        </div>
        <div className="absolute left-0 top-0 h-full" style={{ width: gapPx, background: "#8c8983", transformOrigin: "left center", transform: `translateZ(${gapPx - 0.5}px) rotateY(90deg)` }} />
        <div className="absolute left-0 bottom-0 w-full" style={{ height: gapPx, background: "#77746f", transformOrigin: "center bottom", transform: `translateZ(${gapPx - 0.5}px) rotateX(90deg)` }} />
      </div>
      <div className="absolute inset-0" style={{ transformStyle: "preserve-3d", transformOrigin: "50% 0%", transform: `translate3d(0, ${-lift}px, ${zFront}px) rotateX(${st.lift * 7}deg)` }}>
        <div className="absolute left-0 h-full" style={{ top: 0, width: edge, background: "linear-gradient(#e4e2de,#b3afa8)", borderRadius: 1, opacity: st.op, transformOrigin: "left center", transform: "rotateY(90deg)" }} />
        <div className="absolute left-0 w-full" style={{ bottom: 0, height: edge, background: "#bdb9b2", borderRadius: 1, opacity: st.op, transformOrigin: "center bottom", transform: "rotateX(90deg)" }} />
        <div className="absolute inset-0 overflow-hidden" style={{ borderRadius: r, opacity: st.op, background: "#ddd" }}>
          <img src={p.src} alt={p.alt} draggable={false} className="h-full w-full select-none object-cover" style={{ objectPosition: p.pos }} />
          {st.sweep >= 0 && (
            <div aria-hidden data-testid="hero-reflection" className="pointer-events-none absolute inset-0" style={{
              backgroundImage: "linear-gradient(135deg, transparent 42%, rgba(255,255,255,0.08) 46%, rgba(255,255,255,0.32) 50%, rgba(255,255,255,0.08) 54%, transparent 58%)",
              backgroundSize: "300% 300%", backgroundPosition: `${100 - st.sweep * 100}% ${100 - st.sweep * 100}%`,
            }} />
          )}
        </div>
      </div>
    </div>
  );
};

const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(() => window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  useEffect(() => {
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    const fn = (e) => setReduced(e.matches);
    m.addEventListener("change", fn);
    return () => m.removeEventListener("change", fn);
  }, []);
  return reduced;
};

export const WallGallery = () => {
  const box = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const reduced = usePrefersReducedMotion();
  const t = useClock(reduced);
  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setSize({ w: e.contentRect.width, h: e.contentRect.height }));
    ro.observe(box.current);
    return () => ro.disconnect();
  }, []);
  const { media, ready } = useSiteMediaState();
  const isMobile = size.w && size.w / size.h < 0.9;
  const base = isMobile ? GALLERY.mobile : GALLERY.desktop;
  const bg = media[isMobile ? "hero-wall-mobile" : "hero-wall-desktop"]?.url || base.bg;
  const layout = { ...base, pieces: base.pieces.map((p) => { const m = media[`hero-${p.id}`]; return m ? { ...p, src: m.url, alt: m.alt, pos: m.pos || p.pos } : p; }) };
  const b = bounds(layout.pieces);
  const s = size.w ? Math.min((size.w * layout.maxW) / (b.x1 - b.x0), (size.h * layout.maxH) / (b.y1 - b.y0)) : 0;
  const ox = size.w * layout.cx - ((b.x0 + b.x1) / 2) * s;
  const oy = size.h * layout.cy - ((b.y0 + b.y1) / 2) * s;
  const gapPx = GAP_CM * s;

  return (
    <div ref={box} data-testid="hero-wall-gallery" data-reduced={reduced ? "true" : "false"} className="absolute inset-0 overflow-hidden"
      role="img" aria-label="Pared con ocho láminas magnéticas flotantes LUMINA que aparecen una a una">
      {ready && <img data-testid="hero-wall-bg" src={bg} alt="" className="absolute inset-0 h-full w-full object-cover" />}
      {ready && s > 0 && (
        <div className="absolute inset-0" style={{ perspective: `${Math.max(700, size.w * 0.95)}px`, perspectiveOrigin: "2% 30%" }}>
          <div className="absolute" style={{ left: ox, top: oy, transformStyle: "preserve-3d" }}>
            {layout.pieces.map((p, i) => (
              <Piece key={p.id} p={p} s={s} gapPx={gapPx} st={{ ...pieceState(i, layout.pieces.length, t, p.id === layout.demoId, reduced), liftK: layout.liftK }} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
